import { useEffect, useRef } from 'react'
import { pickAudioContextCtor } from './audioContext'

// Compressor params tuned specifically for live SIM-call audio. The
// source is AoC-band-limited carrier (~3-4 kHz cutoff) with an uneven
// noise floor and harsh peaks when boosted. Differs from
// useAudioBoost's COMPRESSOR_PARAMS (used for clean recorded audio):
// - threshold lower (-12 vs -3): catch peaks earlier so they don't
//   reach the destination as harsh clips at 250-300% gain
// - ratio higher (6 vs 4): firmer squash on the peaks that DO arrive,
//   less "rasp" on consonants
// - knee 8 dB: softer engagement than hard knee, less audible "click"
//   when compression kicks in mid-word
// - release 100 ms (vs 50): slower recovery, no audible "pumping" on
//   start/stop of speech
// - attack 3 ms: unchanged — fast enough for voice transients
const CALL_COMPRESSOR_PARAMS = {
  threshold: -12,
  ratio: 6,
  attack: 0.003,
  release: 0.1,
  knee: 8,
}

// Web Audio graph for the LIVE call's remote audio:
//   MediaStreamSource → GainNode → [DynamicsCompressor when gain>1] → destination
//
// The hook is driven by `enabled` (master switch) + `stream` (current
// remote MediaStream from pc.ontrack) + `gain` (slider 1.0..3.0). The
// <audio> element keeps the RTP pipeline pumping (Chrome quirk: a
// remote-track MediaStream connected only via createMediaStreamSource
// stays silent unless the same stream is also attached to a playing
// <audio>); we mute it while the graph plays so the user doesn't hear
// the same audio twice.
//
// Lifecycle:
//   - enabled=true && stream != null → build graph, mute element
//   - enabled=false                  → tear down, unmute element
//   - stream → null (call ended)     → tear down, unmute element
//   - stream → S2 (renegotiation)    → tear down S1 graph, build S2 graph
//   - unmount                        → tear down (return cleanup)
//
// Three negative-scenario invariants:
//   1. Serialized close→build via closingPromiseRef — rapid toggle never
//      leaks AudioContexts (browsers cap ~6 per origin).
//   2. Autoplay-aware muting — if ctx stays 'suspended' (no user gesture
//      yet), DON'T mute the element; the native path is the fallback so
//      the operator hears something. Re-resume on next gesture.
//   3. Null-safe teardown — webrtc.cleanup detaches the audio element
//      synchronously while our teardown runs later (commit-phase). The
//      audioElRef.current may be null by then; guard before muted=false.

interface Graph {
  ctx: AudioContext
  source: MediaStreamAudioSourceNode
  gain: GainNode
  compressor: DynamicsCompressorNode
  compressorEngaged: boolean
  stream: MediaStream
}

const RAMP_TIME_CONSTANT = 0.02 // 20ms — standard for volume sliders, no zipper noise

function noop(): void {
  /* swallow */
}

/**
 * Wires GainNode + optional Compressor between the WebRTC remote stream
 * and the audio output. Returns nothing — the caller drives it via
 * `enabled` and `gain` props.
 *
 * `audioElRef` MUST be the same `<audio>` element webrtc.ts attached
 * the stream to. We mute that element while the graph plays, so audio
 * doesn't double up.
 */
export function useCallAudioBoost(
  audioElRef: React.RefObject<HTMLAudioElement | null>,
  stream: MediaStream | null,
  enabled: boolean,
  gain: number,
): void {
  const graphRef = useRef<Graph | null>(null)
  // Tracks the in-flight ctx.close() promise from the previous teardown.
  // Build awaits it so AudioContext instances close in order; rapid
  // toggle ON-OFF-ON cannot stack contexts.
  const closingPromiseRef = useRef<Promise<void> | null>(null)
  // Latest gain so the build effect (which reads at fire-time) gets the
  // current value. Mirrors useAudioBoost.ts's gainRef.
  const gainRef = useRef(gain)
  useEffect(() => {
    gainRef.current = gain
  }, [gain])

  // Build / teardown lifecycle. Re-runs on enabled OR stream change.
  // useEffect cleanup fires before the next effect call (or on unmount),
  // so renegotiation S1→S2 (with or without an intermediate null) tears
  // down S1's graph before building S2's.
  useEffect(() => {
    const audio = audioElRef.current
    const ctxCtor = pickAudioContextCtor()

    // No work to do if disabled or no stream yet.
    if (!enabled || stream === null) {
      return
    }

    // jsdom / unsupported — silently no-op. The native <audio> element
    // continues to play at its native volume; no boost is possible here.
    if (!ctxCtor) {
      return
    }

    let cancelled = false

    const build = async (): Promise<void> => {
      // Wait for any in-flight close from a prior teardown so we don't
      // stack AudioContexts on rapid toggle. catch() above keeps the
      // chain alive even if close() rejected (unlikely).
      if (closingPromiseRef.current) {
        await closingPromiseRef.current
        closingPromiseRef.current = null
      }
      if (cancelled) return

      let ctx: AudioContext
      try {
        ctx = new ctxCtor()
      } catch {
        return
      }

      let source: MediaStreamAudioSourceNode
      try {
        source = ctx.createMediaStreamSource(stream)
      } catch {
        // Stream may have been ended between cleanup and build (rare).
        // Record the close into closingPromiseRef so the next build
        // awaits it — graphRef wasn't set, so the cleanup path won't.
        closingPromiseRef.current = ctx.close().catch(noop)
        return
      }

      const gainNode = ctx.createGain()
      gainNode.gain.value = gainRef.current

      const compressor = ctx.createDynamicsCompressor()
      compressor.threshold.value = CALL_COMPRESSOR_PARAMS.threshold
      compressor.ratio.value = CALL_COMPRESSOR_PARAMS.ratio
      compressor.attack.value = CALL_COMPRESSOR_PARAMS.attack
      compressor.release.value = CALL_COMPRESSOR_PARAMS.release
      compressor.knee.value = CALL_COMPRESSOR_PARAMS.knee

      source.connect(gainNode)
      const engageCompressor = gainRef.current > 1
      if (engageCompressor) {
        gainNode.connect(compressor)
        compressor.connect(ctx.destination)
      } else {
        gainNode.connect(ctx.destination)
      }

      // Try to resume — required by autoplay policy. If we're not on a
      // user gesture (e.g. after a refresh into an active call), this
      // resolves with state still 'suspended' or rejects. In that case
      // we leave the native <audio> element UNMUTED so the operator
      // still hears the call via the default path. Next user gesture
      // (slider/toggle click) re-runs this effect and tries again.
      try {
        await ctx.resume()
      } catch {
        /* state stays suspended — handled below */
      }
      if (cancelled) {
        // Cancelled while paused on resume(). cleanup ran with graphRef
        // still null (graph not yet set), so it took the early-return
        // path and didn't record a close promise. We MUST record here —
        // otherwise the next build won't await this close, two contexts
        // can briefly coexist, and rapid toggle leaks contexts.
        closingPromiseRef.current = ctx.close().catch(noop)
        return
      }

      const graph: Graph = {
        ctx, source, gain: gainNode, compressor,
        compressorEngaged: engageCompressor,
        stream,
      }
      graphRef.current = graph

      // Mute the native path ONLY if the context is actually running.
      // Otherwise (autoplay block) the graph isn't playing anyway and
      // muting would silence the operator's last audible source.
      if (ctx.state === 'running' && audio) {
        audio.muted = true
      }
    }

    void build()

    return () => {
      cancelled = true
      const g = graphRef.current
      graphRef.current = null
      if (!g) return

      // Unmute BEFORE disconnecting so the native path is alive during
      // the (microtask-scale) gap. We INTENTIONALLY read audioElRef.current
      // at cleanup time (not snapshot at effect start): webrtc.cleanup may
      // have detached/nulled the element by now, in which case skipping
      // the muted=false is correct — there's nothing to unmute.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const a = audioElRef.current
      if (a) {
        try { a.muted = false } catch { /* element gone */ }
      }

      try { g.source.disconnect() } catch { /* ignore */ }
      try { g.gain.disconnect() } catch { /* ignore */ }
      try { g.compressor.disconnect() } catch { /* ignore */ }
      // Track the close promise so the next build awaits it.
      closingPromiseRef.current = g.ctx.close().catch(noop)
    }
  }, [enabled, stream, audioElRef])

  // Runtime gain change. Two paths:
  //   - graph live → ramp via setTargetAtTime, re-wire compressor on
  //     boundary cross
  //   - no graph    → no-op (next build picks up gainRef.current)
  useEffect(() => {
    const g = graphRef.current
    if (!g) return

    g.gain.gain.setTargetAtTime(gain, g.ctx.currentTime, RAMP_TIME_CONSTANT)

    const wantCompressor = gain > 1
    if (wantCompressor !== g.compressorEngaged) {
      try { g.gain.disconnect() } catch { /* ignore */ }
      if (wantCompressor) {
        g.gain.connect(g.compressor)
        try { g.compressor.disconnect() } catch { /* ignore */ }
        g.compressor.connect(g.ctx.destination)
      } else {
        try { g.compressor.disconnect() } catch { /* ignore */ }
        g.gain.connect(g.ctx.destination)
      }
      g.compressorEngaged = wantCompressor
    }
  }, [gain])
}
